/**
 * Logo — code-native pixel logo for the game (same bitmap-to-<rect> technique as
 * CardGlyph / SeatGlyph / PixelIcon: no external image assets, no runtime
 * drawing). The mark is a single creature sliced in half at the waist: the top
 * half is the gold invader, the bottom half is the pink rival skull — "half
 * yours, half theirs". `cards` is spelled in a matching 5×7 pixel face.
 *
 * This module is the single source of truth for the brand mark. The standalone
 * `apps/web/public/favicon.svg` (mark only) and `apps/web/public/logo.svg`
 * (full lock-up), and the inline favicon in `index.html`, carry the same
 * bitmaps and colours — keep them in sync when the pixels or tokens change.
 */
import type { ReactElement } from 'react';
import { COLORS } from './kit/tokens.js';

/**
 * Fused mark: 11×10 grid. Rows [0, MARK_SPLIT) are the invader top (gold),
 * rows [MARK_SPLIT, end) are the skull jaw (rival pink) — design system 01 §10.
 */
const MARK_ROWS = [
  '..1.....1..',
  '...1...1...',
  '..1111111..',
  '.11.111.11.',
  '11111111111',
  '.111111111.',
  '.111.1.111.',
  '.111111111.',
  '.1.1.1.1.1.',
  '.1.1.1.1.1.',
] as const;
const MARK_W = MARK_ROWS[0].length;
const MARK_H = MARK_ROWS.length;
const MARK_SPLIT = 5;

/** 5×7 lowercase pixel face for the wordmark (only the glyphs `cards` needs). */
const GLYPH_H = 7;
const GLYPH_W = 5;
const GLYPH_GAP = 1;
const LETTERS: Record<string, readonly string[]> = {
  c: ['.....', '.....', '.111.', '1...1', '1....', '1...1', '.111.'],
  a: ['.....', '.....', '.111.', '....1', '.1111', '1...1', '.1111'],
  // The shoulder reaches the last column so «r» spaces like its neighbours.
  r: ['.....', '.....', '1.111', '11...', '1....', '1....', '1....'],
  d: ['....1', '....1', '.1111', '1...1', '1...1', '1...1', '.1111'],
  s: ['.....', '.....', '.1111', '1....', '.111.', '....1', '1111.'],
};

function markRects(): ReactElement[] {
  return MARK_ROWS.flatMap((row, y) => {
    const fill = y < MARK_SPLIT ? COLORS.logoTop : COLORS.logoBottom;
    return [...row].flatMap((pixel, x) =>
      pixel === '1'
        ? [<rect key={`m${x}:${y}`} x={x} y={y} width="1" height="1" fill={fill} />]
        : []);
  });
}

/** Wordmark rects (in `currentColor`) plus the total advance width they span. */
function wordmarkRects(text: string): { rects: ReactElement[]; width: number } {
  const rects: ReactElement[] = [];
  let ox = 0;
  for (const ch of text) {
    const glyph = LETTERS[ch];
    if (glyph !== undefined) {
      glyph.forEach((row, y) => [...row].forEach((pixel, x) => {
        if (pixel === '1') {
          rects.push(<rect key={`w${ox}:${x}:${y}`} x={ox + x} y={y} width="1" height="1" fill="currentColor" />);
        }
      }));
    }
    ox += GLYPH_W + GLYPH_GAP;
  }
  return { rects, width: Math.max(0, ox - GLYPH_GAP) };
}

export interface LogoMarkProps {
  /** Explicit width in px; omit to let CSS size the SVG. Height keeps the 11:10 ratio. */
  size?: number;
  className?: string;
}

/** The fused invader/skull mark on its own (favicon, header brand, app icon). */
export function LogoMark({ size, className }: LogoMarkProps): ReactElement {
  return (
    <svg
      data-testid="logo-mark"
      viewBox={`0 0 ${MARK_W} ${MARK_H}`}
      {...(size === undefined ? {} : { width: size, height: Math.round((size * MARK_H) / MARK_W) })}
      role="img"
      aria-label="cards"
      focusable="false"
      shapeRendering="crispEdges"
      className={className === undefined ? 'cards-logo-mark' : `cards-logo-mark ${className}`}
    >
      {markRects()}
    </svg>
  );
}

export interface LogoProps {
  /** Explicit height in px; omit to let CSS size the SVG. */
  height?: number;
  className?: string;
}

const WORDMARK_GAP = 3;

/** Full horizontal lock-up: the mark followed by the pixel wordmark `cards`. */
export function Logo({ height, className }: LogoProps): ReactElement {
  const wm = wordmarkRects('cards');
  const totalW = MARK_W + WORDMARK_GAP + wm.width;
  const wordmarkY = Math.round((MARK_H - GLYPH_H) / 2);
  return (
    <svg
      data-testid="logo"
      viewBox={`0 0 ${totalW} ${MARK_H}`}
      {...(height === undefined ? {} : { height, width: Math.round((height * totalW) / MARK_H) })}
      role="img"
      aria-label="cards"
      focusable="false"
      shapeRendering="crispEdges"
      style={{ color: COLORS.textOnDark }}
      className={className === undefined ? 'cards-logo' : `cards-logo ${className}`}
    >
      {markRects()}
      <g transform={`translate(${MARK_W + WORDMARK_GAP}, ${wordmarkY})`}>{wm.rects}</g>
    </svg>
  );
}
